import uniqWith from 'lodash/uniqWith';
import flatMap from 'lodash/flatMap';
import isEqual from 'lodash/isEqual';
import { ViewModelFactoryParams } from '../../../../utils/ControlledComponent/ControlledComponent.types';
import { CalendarState } from '../../controller';
import { FilterOptions } from '../../../../types/types';
import { CalendarContext } from '../../../../utils/context/contextFactory';
import { MemoizedViewModalFactory } from '../viewModel';
import { MAX_STAFF_FILTER_SELECTIONS } from '../../../../constants/constants';
import {
  Service,
  StaffMember,
  WorkingHoursLocationCoverage,
} from '@wix/ambassador-bookings-services-v2-service/types';
import {
  filterNonBusinessLocations,
  getServiceCategoryId,
  getServiceCategoryName,
} from '@wix/bookings-calendar-catalog-viewer-mapper';
import { isCalendarPage, isCalendarWidget } from '../../../../utils/presets';
import { isAnyStaffSentinel } from '../../../../utils/staffSelection/staffSelection';
import {
  PreferenceType,
  DisplayTarget,
  PreferenceToDisplayMapping,
} from '@wix/bookings-services-preferences-modal/utils';
import { ReservedLocationIds } from '@wix/bookings-uou-types';
import {
  SELECTABLE_GENDERS,
  GenderSelectionState,
  isGenderSelectionAvailable,
} from '../../../../utils/genderSelection/genderSource';
import { getAnyGenderStaffFilterLabel } from '../../../../utils/genderSelection/labels';

export enum FilterTypes {
  SERVICE = 'SERVICE',
  LOCATION = 'LOCATION',
  STAFF_MEMBER = 'STAFF_MEMBER',
  /** At most one, rendered inside the `STAFF_MEMBER` filter. */
  STAFF_GENDER = 'STAFF_GENDER',
}

export type FilterOption = {
  label: string;
  srOnlyLabel?: string;
  value: string;
  selected: boolean;
  indeterminate?: boolean;
  children?: FilterOption[];
};

export type FilterViewModel = {
  id: FilterTypes;
  label: string;
  options: FilterOption[];
  maxSelectionNumber?: number;
  note?: string;
};

export const memoizedFiltersViewModel: MemoizedViewModalFactory<FilterViewModel> =
  {
    dependencies: {
      // `isRequestedGenderRejected` is declared because the gender chips are
      // built from it: the memoizer compares declared state only, so without it
      // a rejected gender keeps a selected chip on screen until the next
      // unrelated filter change.
      state: ['servicesInView', 'filterOptions', 'isRequestedGenderRejected'],
      settings: [
        'serviceLabel',
        'locationLabel',
        'staffMemberLabel',
        'anyFemaleStaffFilterLabel',
        'anyMaleStaffFilterLabel',
      ],
    },
  };

type CategoryData = { id: string; name: string; services: Service[] };

function getServiceCategories(servicesInView: Service[]): CategoryData[] {
  const categories: Record<string, CategoryData> = {};
  servicesInView.forEach((service) => {
    const category = categories[getServiceCategoryId(service)!];
    if (category) {
      category.services.push(service);
    } else {
      categories[getServiceCategoryId(service)!] = {
        id: getServiceCategoryId(service)!,
        name: getServiceCategoryName(service)!,
        services: [service],
      };
    }
  });
  const sortedCategories = Object.values(categories);
  sortedCategories.sort(({ name: firstCategory }, { name: secondCategory }) =>
    firstCategory.localeCompare(secondCategory),
  );
  sortedCategories.forEach(({ services }) =>
    services.sort((firstService, secondService) =>
      firstService.name!.localeCompare(secondService.name!),
    ),
  );

  return sortedCategories;
}

function getServiceFilterOptions(
  categories: CategoryData[],
  selectedServices: string[],
  context: CalendarContext,
): FilterOption[] {
  const selectedServicesSet = new Set(selectedServices);
  const serviceToFilterOption = (service: Service): FilterOption => ({
    value: service.id!,
    label: service.name!,
    selected: selectedServicesSet.has(service.id!),
  });

  if (categories.length === 1) {
    return categories[0].services.map(serviceToFilterOption);
  } else {
    return categories
      .map(({ name, id, services }): FilterOption => {
        const numberOfSelectedCategoryServices = services.filter((service) =>
          selectedServicesSet.has(service.id!),
        ).length;

        return {
          value: id,
          label: name,
          srOnlyLabel: context.t(
            'filter.accessibility.service-filter.parent-label',
            { categoryName: name },
          ),
          selected: services.length === numberOfSelectedCategoryServices,
          indeterminate:
            numberOfSelectedCategoryServices > 0 &&
            numberOfSelectedCategoryServices < services.length,
          children: services.map(serviceToFilterOption),
        };
      })
      .flat();
  }
}

function createServiceFilterViewModel(
  state: CalendarState,
  context: CalendarContext,
): FilterViewModel | undefined {
  const { getContent, reportError, settingsParams } = context;
  const { servicesInView, filterOptions } = state;

  try {
    if (servicesInView.length > 1) {
      const categories = getServiceCategories(servicesInView);
      const options = getServiceFilterOptions(
        categories,
        filterOptions.SERVICE,
        context,
      );
      return {
        label: getContent({
          settingsParam: settingsParams.serviceLabel,
          translationKey: 'app.settings.defaults.service-label',
        }),
        options,
        id: FilterTypes.SERVICE,
      };
    }
  } catch (e) {
    reportError(e as string | Error);
  }
}

function createLocationFilterViewModel(
  state: CalendarState,
  context: CalendarContext,
): FilterViewModel | undefined {
  const { getContent, reportError, settingsParams, preset } = context;
  const { filterOptions, servicesInView } = state;
  const isCalendar = isCalendarPage(preset) || isCalendarWidget(preset);

  try {
    if (!isCalendar) {
      return;
    }

    const availableLocations = getFilterOptions({
      servicesInView,
      optionType: 'locations',
    });
    const availableBusinessLocations =
      filterNonBusinessLocations(availableLocations);

    if (availableBusinessLocations.length > 1) {
      return {
        label: getContent({
          settingsParam: settingsParams.locationLabel,
          translationKey: 'app.settings.defaults.location-label',
        }),
        options: availableLocations
          .sort((firstLocation, secondLocation) =>
            (firstLocation.business?.name || '').localeCompare(
              secondLocation.business?.name || '',
            ),
          )
          .map((location) => ({
            selected: filterOptions.LOCATION.includes(location.id!),
            label: location.business?.name || '',
            value: location.id || '',
          })),
        id: FilterTypes.LOCATION,
      };
    }
  } catch (e) {
    reportError(e as string | Error);
  }
}

export function createGenderFilterOptions({
  selectedGenders,
  state,
  context,
}: {
  selectedGenders: string[];
  state: GenderSelectionState;
  context: CalendarContext;
}): FilterOption[] {
  if (!isGenderSelectionAvailable({ state, context })) {
    return [];
  }

  return SELECTABLE_GENDERS.map((gender) => ({
    value: gender,
    label: getAnyGenderStaffFilterLabel({ gender, context }),
    selected: selectedGenders.includes(gender),
  }));
}

function createStaffMemberFilterViewModel(
  state: CalendarState,
  context: CalendarContext,
): FilterViewModel | undefined {
  const { getContent, reportError, settingsParams } = context;
  const { filterOptions, servicesInView } = state;

  try {
    if (isAnyStaffSentinelSelected(filterOptions)) {
      return;
    }

    const availableStaffMembers = getSelectableStaffMembers({
      servicesInView,
      chosenLocationIds: filterOptions.LOCATION,
    });

    if (availableStaffMembers.length > 1) {
      return {
        label: getContent({
          settingsParam: settingsParams.staffMemberLabel,
          translationKey: 'app.settings.defaults.staff-member-label',
        }),
        options: [
          ...createGenderFilterOptions({
            selectedGenders: filterOptions.STAFF_GENDER,
            state,
            context,
          }),
          ...availableStaffMembers
            .sort(({ name: firstStaffName }, { name: secondStaffName }) =>
              firstStaffName!.localeCompare(secondStaffName!),
            )
            .map((staffMember) => ({
              selected: filterOptions.STAFF_MEMBER.includes(
                staffMember.staffMemberId!,
              ),
              label: staffMember.name!,
              value: staffMember.staffMemberId!,
            })),
        ],
        id: FilterTypes.STAFF_MEMBER,
        maxSelectionNumber: MAX_STAFF_FILTER_SELECTIONS,
      };
    }
  } catch (e) {
    reportError(e as string | Error);
  }
}

/** Only these two can be moved off the calendar; the rest always belong to it. */
const MOVABLE_FILTER_PREFERENCES: Partial<Record<FilterTypes, PreferenceType>> =
  {
    [FilterTypes.STAFF_MEMBER]: PreferenceType.STAFF_MEMBER,
    [FilterTypes.LOCATION]: PreferenceType.LOCATION,
  };

export const isFilterDisplayedOnCalendar = ({
  filterType,
  appointmentPreferencesDisplay,
}: {
  filterType: FilterTypes;
  appointmentPreferencesDisplay?: PreferenceToDisplayMapping;
}): boolean => {
  const preferenceType = MOVABLE_FILTER_PREFERENCES[filterType];

  return (
    !appointmentPreferencesDisplay ||
    !preferenceType ||
    appointmentPreferencesDisplay[preferenceType] === DisplayTarget.CALENDAR
  );
};

const filterFilterViewModelsByPreferencesDisplay = ({
  filterViewModels,
  appointmentPreferencesDisplay,
}: {
  filterViewModels: FilterViewModel[];
  appointmentPreferencesDisplay?: PreferenceToDisplayMapping;
}): FilterViewModel[] =>
  filterViewModels.filter(({ id }) =>
    isFilterDisplayedOnCalendar({
      filterType: id,
      appointmentPreferencesDisplay,
    }),
  );

export function createFilterViewModels({
  state,
  context,
}: ViewModelFactoryParams<CalendarState, CalendarContext>): FilterViewModel[] {
  const { appointmentPreferencesDisplay } = state;
  const notUndefined = <T>(value: T): value is NonNullable<T> =>
    value !== undefined;

  const allFilterViewModels = [
    createServiceFilterViewModel(state, context),
    createLocationFilterViewModel(state, context),
    createStaffMemberFilterViewModel(state, context),
  ].filter(notUndefined);

  return filterFilterViewModelsByPreferencesDisplay({
    filterViewModels: allFilterViewModels,
    appointmentPreferencesDisplay,
  });
}

type FilterOptionType = Extract<keyof Service, 'staffMembers' | 'locations'>;

const getFilterOptions = <T extends FilterOptionType>({
  servicesInView,
  optionType,
}: {
  servicesInView: Service[];
  optionType: T;
}): NonNullable<Service[T]> => {
  return uniqWith(
    flatMap(servicesInView, optionType).filter(Boolean),
    isEqual,
  ) as NonNullable<Service[T]>;
};

/**
 * The sentinel replaces the staff filter rather than selecting inside it, so
 * nothing that depends on the filter being on screen may act while it is set.
 */
export const isAnyStaffSentinelSelected = (
  filterOptions: FilterOptions,
): boolean => isAnyStaffSentinel(filterOptions[FilterTypes.STAFF_MEMBER]);

/**
 * The pool the staff filter offers. Below two members it renders nothing, so
 * anything acting on a staff selection has to reach the same verdict.
 */
export const getSelectableStaffMembers = ({
  servicesInView,
  chosenLocationIds,
}: {
  servicesInView: Service[];
  chosenLocationIds: string[];
}): StaffMember[] =>
  filterStaffMembersByChosenLocation({
    staffMembers: getFilterOptions({
      servicesInView,
      optionType: 'staffMembers',
    }),
    chosenLocationIds,
  });

const filterStaffMembersByChosenLocation = ({
  staffMembers,
  chosenLocationIds,
}: {
  staffMembers: StaffMember[];
  chosenLocationIds: string[];
}): StaffMember[] => {
  const businessLocationIds = chosenLocationIds.filter(
    (id) => id && id !== ReservedLocationIds.OTHER_LOCATIONS,
  );

  if (businessLocationIds.length === 0) {
    return staffMembers;
  }

  return staffMembers.filter((staffMember) =>
    isStaffAvailableAtChosenLocations(
      staffMember.workingHoursLocationCoverage,
      businessLocationIds,
    ),
  );
};

const isStaffAvailableAtChosenLocations = (
  coverage: WorkingHoursLocationCoverage | undefined,
  chosenBusinessLocationIds: string[],
): boolean => {
  // Omitted when enrichment was not requested (e.g. FT off on queryServices).
  if (!coverage) {
    return true;
  }

  if (coverage.availableAtAllLocations) {
    return true;
  }

  const locationIds = coverage.locationIds ?? [];
  if (locationIds.length === 0) {
    return false;
  }

  return locationIds.some((locationId) =>
    chosenBusinessLocationIds.includes(locationId),
  );
};
